/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Divider,
  Popover,
  Rating,
  Skeleton,
  styled,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  tooltipClasses,
  TooltipProps,
} from '@mui/material';
import {
  Link,
  useNavigate,
} from 'react-router-dom';
import {
  useAuth,
  useApplications,
  hasModuleAccess,
} from '@panna/auth';
import { FiPlusCircle } from 'react-icons/fi';
import { SlLocationPin } from 'react-icons/sl';
import { LuPhone } from 'react-icons/lu';
import { MdOutlineMailOutline } from 'react-icons/md';
import { IoMdEye } from 'react-icons/io';
import { Module } from 'src/access';
import { StatusDropdown } from 'src/components';
import { CloseIcon, NewTabIcon } from 'src/icons';
import { useLocations } from 'src/hooks/query/locations';
import { tabsList } from '../../constants';
import { ViewJobCandidate } from '../../types';
import ConsultancyPopup from '../ConsultancyPopup';
import copyTextToClipboard from '../../utils';

function CustomTabPanel(props: any) {
  const {
    children,
    value,
    tabName,
    ...other
  } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== tabName}
      id={`tabpanel-${tabName}`}
      aria-labelledby={`tab-${tabName}`}
      {...other}
    >
      {value === tabName && (
        <Box>
          {children}
        </Box>
      )}
    </div>
  );
}

const CandidateCardTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#fff',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #000',
  },
}));

const CandidateTile = function (props: ViewJobCandidate) {
  const {
    candidateId,
    candidateStatus,
    firstName = '',
    middleName = '',
    lastName = '',
    candidateLocation = '',
    phoneNumber = '',
    emailAddress = '',
    hashKey = '',
    jobOrderId,
    fetchCandidates,
    assignedToAll,
    relevanceScore,
    techSkill,
    communicationSkills,
    score,
    interviewComments,
    pannaInterviewComments,
    pannaReportURL,
    mliveReportURL,
    consultancyInfo,
    type,
  } = props;

  const applications = useApplications();
  const auth = useAuth();
  const { authState } = auth;
  const isVirtualRecruiter = hasModuleAccess(
    authState?.userInfo?.modules || [],
    Module.VR_ASSISTANT,
  );

  const { applications: applicationsList } = applications;
  const vrData = applicationsList && applicationsList.find((application) => (
    application.name === 'VirtualRecruiter'
  ));
  const { url: vrUrl } = vrData || {};

  const getFullName = () => [firstName, middleName, lastName].filter(Boolean).join(' ');
  const fullName = getFullName();
  const [copiedEmail, setCopiedEmail] = useState<boolean>(false);
  const [copiedPhone, setCopiedPhone] = useState<boolean>(false);

  const [
    consultancyAnchorEl, setConsultancyAnchorEl,
  ] = React.useState<HTMLElement | null>(null);

  const { locationsOptions } = useLocations();

  const copyEmail = (text: string) => {
    copyTextToClipboard(text);
    setCopiedEmail(true);
    setTimeout(() => {
      setCopiedEmail(false);
    }, 1000);
  };

  const copyPhone = (text: string) => {
    copyTextToClipboard(text);
    setCopiedPhone(true);
    setTimeout(() => {
      setCopiedPhone(false);
    }, 500);
  };

  const openVrTab = () => {
    const preparedUrl = `${vrUrl}?candidate=${hashKey}&jobId=${jobOrderId}&type=${type}`;
    if (preparedUrl) {
      // window.open(preparedUrl, `panna-recruit-${vrUrl}`);
      window.open(preparedUrl, '_blank');
    }
  };

  const openVrAsConsultancy = (consultancyEmailAddress: string) => {
    const preparedUrl = `${vrUrl}?candidate=${hashKey}&jobId=${jobOrderId}&consultancyEmail=${consultancyEmailAddress}`;
    if (preparedUrl) {
      // window.open(preparedUrl, `panna-recruit-${vrUrl}`);
      window.open(preparedUrl, '_blank');
    }
  };

  return (
    <Box
      className="bg-white text-left p-3 m-1"
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Box className="relative">
        <Box>
          <Box className="font-bold text-sm pb-4 flex justify-between w-[80%]">
            <div className="truncate">{fullName}</div>
            <Tooltip title="employment Type" className="hover:cursor-pointer">
              <div className="text-primary">{type?.toUpperCase()}</div>
            </Tooltip>
          </Box>
          <div className="grid gap-2 grid-cols-1 pb-4">
            <div className="flex items-center gap-2">
              <SlLocationPin width={14} />
              <div className="text-xs">{locationsOptions.find((option) => option?.value === candidateLocation)?.label || ''}</div>
            </div>
            {phoneNumber && (
              isVirtualRecruiter ? (
                <div className="flex gap-1">
                  <Tooltip open={copiedPhone} title="Copied" followCursor>
                    <div
                      className="cursor-pointer hover:bg-[#ddd]"
                      onClick={() => copyPhone(phoneNumber)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          copyPhone(phoneNumber);
                        }
                      }}
                    >
                      <div
                        className="no-underline text-[#000] flex items-center gap-2"
                        onClick={openVrTab}
                      >
                        <LuPhone width={14} />
                        <div className="text-xs">{phoneNumber}</div>
                      </div>
                    </div>
                  </Tooltip>
                </div>
              ) : (
                <div className="flex gap-1">
                  <Tooltip open={copiedPhone} title="Copied" followCursor>
                    <div
                      className="flex items-center gap-2 cursor-pointer hover:bg-[#ddd]"
                      onClick={() => copyPhone(phoneNumber)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          copyPhone(phoneNumber);
                        }
                      }}
                    >
                      <LuPhone width={14} />
                      <div className="text-xs">{phoneNumber}</div>
                    </div>
                  </Tooltip>
                </div>
              )
            )}
            <div className="flex gap-1">
              <Tooltip open={copiedEmail} title="Copied" followCursor>
                <div
                  className="flex gap-2 cursor-pointer hover:bg-[#ddd]"
                  onClick={() => copyEmail(emailAddress)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      copyEmail(emailAddress);
                    }
                  }}
                >
                  <MdOutlineMailOutline width={14} />
                  <div className="text-xs">{emailAddress}</div>
                </div>
              </Tooltip>
            </div>
            {
              type?.toLowerCase() === 'c2c' && consultancyInfo && (
              <div className="flex gap-1">
                <Popover
                  open={Boolean(consultancyAnchorEl)}
                  anchorEl={consultancyAnchorEl}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  onClose={(e) => {
                    if (e) {
                      (e as Event).stopPropagation();
                    }
                    setConsultancyAnchorEl(null);
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div
                    className="absolute right-2 top-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      setConsultancyAnchorEl(null);
                    }}
                  >
                    <CloseIcon className="cursor-pointer" />
                  </div>
                  <ConsultancyPopup
                    consultancyInfo={consultancyInfo}
                    isVirtualRecruiter={isVirtualRecruiter}
                    openVrTab={openVrAsConsultancy}
                  />
                </Popover>
                <Tooltip title="employer info" followCursor>
                  <div
                    className="flex gap-2 cursor-pointer hover:bg-[#ddd]"
                    onClick={(event) => {
                      event.stopPropagation();
                      event.preventDefault();
                      setConsultancyAnchorEl(event.currentTarget);
                    }}
                  >
                    <IoMdEye width={14} className="text-primary" />
                    <div className="text-xs text-primary">Employer Details</div>
                  </div>
                </Tooltip>
              </div>
              )
            }
          </div>
        </Box>

        <Box className="text-center cursor-pointer">
          <Link
            to={`/candidate-profile/${hashKey}/info`}
            target="_blank"
            className="no-underline text-black"
          >
            <Tooltip placement="top" title="Open Candidate Profile">
              <div className="cursor-pointer absolute top-1 right-0">
                <NewTabIcon className="fill-primary" height={16} width={16} />
              </div>
            </Tooltip>
          </Link>
        </Box>
      </Box>
      <Box>
        <StatusDropdown
          jobOrderId={Number(jobOrderId)}
          candidateId={candidateId}
          candidateStatus={candidateStatus}
          emailIds={[emailAddress]}
          candidateName={fullName}
          resumeData={{ ...props }}
          onSuccess={() => fetchCandidates(assignedToAll)}
        />
      </Box>
      <div className="flex pt-2 gap-2 text-center cursor-pointer">
        {relevanceScore ? (
          <Tooltip placement="top" title="Relevance Score">
            <div className="flex flex-col cursor-default border border-solid rounded-md border-[#bdbdbd] p-1 items-center gap-2">
              <div className="text-xs">
                Relevance score
              </div>
              <div className="text-sm font-semibold">
                {relevanceScore}
              </div>
            </div>
          </Tooltip>
        ) : null}
        {pannaReportURL ? (
          <CandidateCardTooltip
            placement="top"
            title={<div dangerouslySetInnerHTML={{ __html: pannaInterviewComments }} />}
            arrow
          >
            <div
              className="flex border border-solid rounded-md border-[#bdbdbd] p-1 flex-col items-center"
              onClick={() => window.open(pannaReportURL, '_blank')}
            >
              <div className="text-xs">
                Paññã score
              </div>
              <div className="text-sm font-semibold">
                {score}
              </div>
            </div>
          </CandidateCardTooltip>
        ) : null}
        {mliveReportURL ? (
          <CandidateCardTooltip
            placement="top"
            arrow
            title={<div dangerouslySetInnerHTML={{ __html: interviewComments }} />}
          >
            <div
              className="text-xs border border-solid rounded-md border-[#bdbdbd] p-1 flex flex-col"
              onClick={() => window.open(mliveReportURL, '_blank')}
            >
              <div className="flex items-left">
                <div className="text-[10px] text-left w-[40px]">
                  Tech.
                </div>
                <div className="text-sm font-semibold">
                  <Rating
                    value={techSkill}
                    precision={0.5}
                    readOnly
                    size="small"
                    sx={{
                      fontSize: '0.8rem',
                      gap: 0,
                    }}
                  />
                </div>
              </div>
              <div className="flex items-center">
                <div className="text-[10px] text-left w-[40px]">
                  Comm.
                </div>
                <div className="text-sm font-semibold">
                  <Rating
                    value={communicationSkills}
                    precision={0.5}
                    readOnly
                    size="small"
                    sx={{
                      fontSize: '0.8rem',
                      gap: 0,
                    }}
                  />
                </div>
              </div>
            </div>
          </CandidateCardTooltip>
        ) : null}
      </div>
    </Box>
  );
};

const CandidateCardSkelton = function () {
  return (
    <Box
      className="bg-white text-left p-3 m-1"
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Skeleton className="w-[70%] h-[30px]" />
      <div className="flex gap-2 items-center">
        <Skeleton variant="circular" className="w-[16px] h-[16px]" />
        <Skeleton variant="text" className="w-[40%]" />
      </div>
      <div className="flex gap-2 items-center">
        <Skeleton variant="circular" className="w-[16px] h-[16px]" />
        <Skeleton variant="text" className="w-[30%]" />
      </div>
      <div className="flex gap-2 items-center">
        <Skeleton variant="circular" className="w-[16px] h-[16px]" />
        <Skeleton variant="text" className="w-[60%]" />
      </div>
      <Skeleton className="w-[100%] h-[50px]" />
    </Box>
  );
};

const AppliedResume = function (props: any) {
  const {
    candidatesLoading,
    candidatesList = [],
    fetchCandidates,
    jobOrderId,
    assignedToAll,
    setAssignedToAll,
  } = props;

  const navigate = useNavigate();
  const authContext = useAuth();
  const { authState: auth } = authContext;
  const recruiterId = auth?.userInfo?.id || '';

  useEffect(() => {
    if (fetchCandidates) {
      fetchCandidates(assignedToAll);
    }
  }, [assignedToAll]);

  const [search, setSearch] = useState('');

  const filteredCandidates = candidatesList?.filter((candidate: any) => {
    if (!candidate) return false;

    const {
      firstName = '', middleName = '', lastName = '', emailAddress = '',
    } = candidate;

    const fullName = [firstName, middleName, lastName].filter(Boolean).join(' ').toLowerCase();
    const searchTerm = search.toLowerCase();

    return (
      fullName.includes(searchTerm)
      || emailAddress.toLowerCase().includes(searchTerm)
    );
  }) || [];

  return (
    <>
      <Box className="flex justify-between gap-4 text-left py-2 px-4 items-center bg-white">
        <TextField
          size="small"
          placeholder="Search profile"
          className="w-full text-xs h-10"
          onChange={
            (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
              setSearch(e.target.value || '');
            }
          }
        />
        <div className="flex justify-center items-center text-center">
          <div>Me</div>
          <Switch
            checked={assignedToAll}
            onChange={(e: any) => setAssignedToAll(e.target.checked)}
            size="small"
          />
          <div>All</div>
        </div>
      </Box>
      <Divider />
      {candidatesLoading ? (
        <div className="py-4 text-md w-full gap-2 items-center justify-center">
          <CandidateCardSkelton />
          <CandidateCardSkelton />
        </div>
      ) : (
        <Box className="bg-lightGray h-[calc(100vh-300px)] overflow-y-auto">
          <div className="flex flex-col bg-lightGray gap-1">
            {
              filteredCandidates.map((item: any) => (
                <CandidateTile
                  key={item?.id}
                  {...item}
                  jobOrderId={jobOrderId}
                  fetchCandidates={fetchCandidates}
                  assignedToAll={assignedToAll}
                />
              ))
            }
          </div>
          <Button
            variant="text"
            startIcon={<FiPlusCircle />}
            size="small"
            onClick={() => {
              navigate(
                '/talent-pool',
                {
                  // include recruiterId if assignedToAll is false
                  state: {
                    jobOrderId,
                    ...(assignedToAll ? {} : { recruiterId }),
                  },
                },
              );
            }}
          >
            View All
          </Button>
        </Box>
      )}
    </>
  );
};

const MatchingResumeTile = function (props: any) {
  const {
    firstName = '',
    middleName = '',
    lastName = '',
    candidateLocation = '',
    phoneNumber = '',
    emailAddress = '',
    hashKey = '',
    type,
    consultancyInfo,
    jobOrderId,
  } = props;

  const getFullName = () => [firstName, middleName, lastName].filter(Boolean).join(' ');
  const fullName = getFullName();
  const [copiedEmail, setCopiedEmail] = useState<boolean>(false);
  const [copiedPhone, setCopiedPhone] = useState<boolean>(false);

  const [
    consultancyAnchorEl, setConsultancyAnchorEl,
  ] = React.useState<HTMLElement | null>(null);

  const copyEmail = (text: string) => {
    copyTextToClipboard(text);
    setCopiedEmail(true);
    setTimeout(() => {
      setCopiedEmail(false);
    }, 1000);
  };

  const copyPhone = (text: string) => {
    copyTextToClipboard(text);
    setCopiedPhone(true);
    setTimeout(() => {
      setCopiedPhone(false);
    }, 500);
  };

  const applications = useApplications();
  const auth = useAuth();
  const { authState } = auth;
  const isVirtualRecruiter = hasModuleAccess(
    authState?.userInfo?.modules || [],
    Module.VR_ASSISTANT,
  );

  const { applications: applicationsList } = applications;
  const vrData = applicationsList && applicationsList.find((application) => (
    application.name === 'VirtualRecruiter'
  ));
  const { url: vrUrl } = vrData || {};

  // const openVrTab = () => {
  //   const preparedUrl = `${vrUrl}?candidate=${hashKey}&jobId=${jobOrderId}`;
  //   if (preparedUrl) {
  //     // window.open(preparedUrl, `panna-recruit-${vrUrl}`);
  //     window.open(preparedUrl, '_blank');
  //   }
  // };

  const openVrAsConsultancy = (consultancyEmailAddress: string) => {
    const preparedUrl = `${vrUrl}?candidate=${hashKey}&jobId=${jobOrderId}&consultancyEmail=${consultancyEmailAddress}`;
    if (preparedUrl) {
      // window.open(preparedUrl, `panna-recruit-${vrUrl}`);
      window.open(preparedUrl, '_blank');
    }
  };

  return (
    <Box
      className="bg-white text-left p-3 m-1"
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Box className="relative">
        <Box>
          <Box className="font-bold text-sm pb-4 flex justify-between w-[80%]">
            <div className="truncate">{fullName}</div>
            <Tooltip title="employment Type" className="hover:cursor-pointer">
              <div className="text-primary">{type?.toUpperCase()}</div>
            </Tooltip>
          </Box>
          <div className="grid gap-2 grid-cols-1 pb-4">
            <div className="flex items-center gap-2">
              <SlLocationPin width={14} />
              <div className="text-xs">{candidateLocation}</div>
            </div>
            {phoneNumber && (
              <div className="flex gap-1">
                <Tooltip open={copiedPhone} title="Copied" followCursor>
                  <div
                    className="flex items-center gap-2 cursor-pointer hover:bg-[#ddd]"
                    onClick={() => copyPhone(phoneNumber)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        copyPhone(phoneNumber);
                      }
                    }}
                  >
                    <LuPhone width={14} />
                    <div className="text-xs">{phoneNumber}</div>
                  </div>
                </Tooltip>
              </div>
            )}
            <div className="flex gap-1">
              <Tooltip open={copiedEmail} title="Copied" followCursor>
                <div
                  className="flex gap-2 cursor-pointer hover:bg-[#ddd]"
                  onClick={() => copyEmail(emailAddress)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      copyEmail(emailAddress);
                    }
                  }}
                >
                  <MdOutlineMailOutline width={14} />
                  <div className="text-xs">{emailAddress}</div>
                </div>
              </Tooltip>
            </div>
            {
              type?.toLowerCase() === 'c2c' && consultancyInfo && (
              <div className="flex gap-1">
                <Popover
                  open={Boolean(consultancyAnchorEl)}
                  anchorEl={consultancyAnchorEl}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  onClose={(e) => {
                    if (e) {
                      (e as Event).stopPropagation();
                    }
                    setConsultancyAnchorEl(null);
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div
                    className="absolute right-2 top-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      setConsultancyAnchorEl(null);
                    }}
                  >
                    <CloseIcon className="cursor-pointer" />
                  </div>
                  <ConsultancyPopup
                    consultancyInfo={consultancyInfo}
                    isVirtualRecruiter={isVirtualRecruiter}
                    openVrTab={openVrAsConsultancy}
                  />
                </Popover>
                <Tooltip title="employer info" followCursor>
                  <div
                    className="flex gap-2 cursor-pointer hover:bg-[#ddd]"
                    onClick={(event) => {
                      event.stopPropagation();
                      event.preventDefault();
                      setConsultancyAnchorEl(event.currentTarget);
                    }}
                  >
                    <IoMdEye width={14} className="text-primary" />
                    <div className="text-xs text-primary">Employer Details</div>
                  </div>
                </Tooltip>
              </div>
              )
            }
          </div>
        </Box>

        <Box className="text-center cursor-pointer">
          <Link
            to={`/candidate-profile/${hashKey}/info`}
            target="_blank"
            className="no-underline text-black"
          >
            <Tooltip placement="top" title="Open Candidate Profile">
              <div className="cursor-pointer absolute top-1 right-0">
                <NewTabIcon className="fill-primary" height={16} width={16} />
              </div>
            </Tooltip>
          </Link>
        </Box>
      </Box>
    </Box>
  );
};

const MatchingResume = function (props: any) {
  const {
    candidatesLoading,
    candidatesList = [],
    fetchCandidates,
    jobOrderId,
  } = props;

  useEffect(() => {
    if (fetchCandidates) {
      fetchCandidates();
    }
  }, []);

  return (
    <>
      <Box className="flex justify-between gap-4 text-left py-2 px-4 items-center bg-white">
        <TextField size="small" placeholder="Search profile" className="w-full text-xs h-10" />
      </Box>
      <Divider />
      {candidatesLoading ? (
        <div className="py-4 text-md w-full gap-2 items-center justify-center">
          <CandidateCardSkelton />
          <CandidateCardSkelton />
        </div>
      ) : (
        <Box className="bg-white">
          <div className="flex flex-col bg-lightGray gap-1">
            {
              candidatesList.map((item: any) => (
                <MatchingResumeTile
                  key={item?.id}
                  {...item}
                  jobOrderId={jobOrderId}
                  fetchCandidates={fetchCandidates}
                />
              ))
            }
          </div>
        </Box>
      )}
    </>
  );
};

const ResumeTabs = function (props: any) {
  const {
    candidatesLoading,
    candidatesList,
    fetchCandidates,
    getMatchingResumes,
    matchingResumeList,
    jobOrderId,
    assignedToAll,
    setAssignedToAll,
    tabValue,
    handleChangeTabValue,
  } = props;
  // const [tabValue, setTabValue] = React.useState(tabsList.appliedResume);

  const handleChange = (event: React.SyntheticEvent, newValue: any) => {
    handleChangeTabValue(newValue);
  };

  return (
    <Box>
      <Tabs
        value={tabValue}
        onChange={handleChange}
        classes={{
          flexContainer: 'justify-center',
        }}
      >
        <Tab
          label={tabsList.appliedResume}
          value={tabsList.appliedResume}
        />
        <Tab
          label={tabsList.matchingResume}
          value={tabsList.matchingResume}
        />
      </Tabs>

      <CustomTabPanel value={tabValue} tabName={tabsList.appliedResume}>
        <AppliedResume
          candidatesLoading={candidatesLoading}
          candidatesList={candidatesList}
          fetchCandidates={fetchCandidates}
          jobOrderId={jobOrderId}
          assignedToAll={assignedToAll}
          setAssignedToAll={setAssignedToAll}
        />
      </CustomTabPanel>
      <CustomTabPanel value={tabValue} tabName={tabsList.matchingResume}>
        <MatchingResume
          candidatesLoading={candidatesLoading}
          candidatesList={matchingResumeList}
          fetchCandidates={getMatchingResumes}
          jobOrderId={jobOrderId}
        />
      </CustomTabPanel>
    </Box>
  );
};

export default ResumeTabs;
